import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  Bold,
  Italic,
  PenLine,
} from "lucide-react";

import SideDrawer from "../UX/SideDrawer";
import { LETTER_FONTS, LETTER_TEXT_STYLES } from "../../utils/letterPreferences";
import {
  execEditorCommand,
  focusAndRestoreSelection,
  getEditorSelectionState,
  materializePendingTypingStyle,
  saveEditorSelection,
} from "../../utils/joditHelpers";

const TEXT_STYLE_LABEL_CLASS = {
  title: "font-logo text-lg leading-tight",
  subtitle: "font-josefin text-base font-medium",
  text: "font-josefin text-sm",
};

function ToolbarIconButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex flex-1 items-center justify-center rounded-md py-2.5 transition duration-200 ${
        active
          ? "bg-primaryText/15 text-primaryText"
          : "text-secondaryText hover:bg-black/[0.04]"
      }`}
    >
      {children}
    </button>
  );
}

function StyleOptionButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-3 text-left transition duration-200 ${
        active
          ? "border-primaryText text-primaryText"
          : "border-transparent text-secondaryText"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Mobile formatting via left drawer.
 * UX priorities: keep keyboard closed, preserve selection, live preview on letter.
 */
function LetterFormattingDrawer({
  open,
  onClose,
  editorInstanceRef,
  preferences,
  onPreferenceChange,
  editorReady,
  readOnly = false,
}) {
  const [activeFormat, setActiveFormat] = useState({
    bold: false,
    italic: false,
    left: false,
    center: true,
  });
  const [selectionPrefs, setSelectionPrefs] = useState({
    fontFamily: preferences.fontFamily,
    textStyle: preferences.textStyle,
  });
  const capturedOnOpenRef = useRef(false);
  const wasOpenRef = useRef(false);

  // Capture the range and its formatting before moving focus to the drawer.
  // On mobile, blurring first can leave only the drawer's selection for this
  // render and makes every command target the wrong place.
  useEffect(() => {
    if (!open) {
      // Only restore caret when the drawer actually closes (Terminé / dismiss).
      // Rematerialize pending typing style so the next click/type keeps it.
      if (wasOpenRef.current && !readOnly) {
        const instance = editorInstanceRef.current;
        if (instance) {
          window.setTimeout(() => {
            focusAndRestoreSelection(instance);
            materializePendingTypingStyle(instance);
          }, 0);
        }
      }
      wasOpenRef.current = false;
      capturedOnOpenRef.current = false;
      return;
    }

    wasOpenRef.current = true;
    if (!editorReady || !editorInstanceRef.current) return;
    if (capturedOnOpenRef.current) return;
    capturedOnOpenRef.current = true;

    const editor = editorInstanceRef.current;
    saveEditorSelection(editor);
    const state = getEditorSelectionState(editor);
    setActiveFormat({
      bold: state.bold,
      italic: state.italic,
      left: state.left,
      center: state.center,
    });
    setSelectionPrefs({
      fontFamily: state.fontFamily || preferences.fontFamily,
      textStyle: state.textStyle || preferences.textStyle,
    });

    const editable = editor.editor;
    const prevInputMode = editable?.getAttribute("inputmode");
    editable?.setAttribute("inputmode", "none");
    try {
      editable?.blur?.();
    } catch {
      // ignore
    }

    return () => {
      if (!editable) return;
      if (prevInputMode != null) {
        editable.setAttribute("inputmode", prevInputMode);
      } else {
        editable.removeAttribute("inputmode");
      }
    };
  // The range/defaults are intentionally captured once per open. Re-running
  // this effect after a font preference save would restore inputmode while
  // the drawer is still visible.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editorReady, editorInstanceRef, readOnly]);

  const syncFromEditor = () => {
    if (!editorReady || !editorInstanceRef.current) return;
    const state = getEditorSelectionState(editorInstanceRef.current);
    setActiveFormat({
      bold: state.bold,
      italic: state.italic,
      left: state.left,
      center: state.center,
    });
    setSelectionPrefs((prev) => ({
      fontFamily: state.fontFamily || prev.fontFamily,
      textStyle: state.textStyle || prev.textStyle,
    }));
  };

  const runCommand = (command, value) => {
    if (!editorReady || readOnly) return false;
    // blurAfter keeps the keyboard closed while the drawer is open; sticky
    // format pins in joditHelpers survive that blur on Safari.
    return execEditorCommand(editorInstanceRef.current, command, value, {
      preserveSelection: true,
      blurAfter: true,
    });
  };

  const toggleFormat = (command) => {
    if (readOnly) return;
    if (runCommand(command)) syncFromEditor();
  };

  const applyAlign = (next) => {
    if (readOnly) return;
    if (runCommand(next.center ? "justifyCenter" : "justifyLeft")) {
      syncFromEditor();
    }
  };

  const applyFont = (fontId) => {
    if (readOnly) return;
    if (!runCommand("fontname", fontId)) return;
    syncFromEditor();
    onPreferenceChange({ fontFamily: fontId }, { applyToWorkspace: true });
    materializePendingTypingStyle(editorInstanceRef.current);
  };

  const applyTextStyle = (style) => {
    if (readOnly) return;
    if (!runCommand("fontsize", style.size)) return;
    syncFromEditor();
  };

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title="Mise en forme"
      footer={
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClose}
          className="w-full rounded-lg bg-primaryDark py-3 font-josefin text-sm font-medium text-white transition hover:bg-primaryText"
        >
          Terminé
        </button>
      }
    >
      <div className={readOnly ? "pointer-events-none opacity-50" : ""}>
        {readOnly && (
          <p className="mb-5 rounded-md bg-white/80 px-3 py-2.5 font-josefin text-xs leading-relaxed text-textMuted">
            Cette lettre est en lecture seule.
          </p>
        )}

        <section className="mb-7">
          <h3 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
            Apparence
          </h3>
          <div className="flex rounded-xl bg-white p-1 shadow-sm">
            <ToolbarIconButton
              active={activeFormat.bold}
              onClick={() => toggleFormat("bold")}
            >
              <Bold size={16} strokeWidth={2.5} />
            </ToolbarIconButton>
            <ToolbarIconButton
              active={activeFormat.italic}
              onClick={() => toggleFormat("italic")}
            >
              <Italic size={16} />
            </ToolbarIconButton>
            <ToolbarIconButton
              active={activeFormat.left}
              onClick={() => applyAlign({ left: true, center: false })}
            >
              <AlignLeft size={16} />
            </ToolbarIconButton>
            <ToolbarIconButton
              active={activeFormat.center}
              onClick={() => applyAlign({ left: false, center: true })}
            >
              <AlignCenter size={16} />
            </ToolbarIconButton>
          </div>
        </section>

        <section className="mb-7">
          <h3 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
            Styles de texte
          </h3>
          <div className="flex flex-col gap-2">
            {LETTER_TEXT_STYLES.map((style) => (
              <StyleOptionButton
                key={style.id}
                active={selectionPrefs.textStyle === style.id}
                onClick={() => applyTextStyle(style)}
              >
                <PenLine size={16} className="shrink-0 text-secondaryText/70" />
                <span className={TEXT_STYLE_LABEL_CLASS[style.id]}>
                  {style.label}
                </span>
              </StyleOptionButton>
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-3 px-1 font-josefin text-xs font-bold uppercase tracking-wider text-secondaryText">
            Police
          </h3>
          <div className="flex flex-col gap-2">
            {LETTER_FONTS.map((font) => (
              <StyleOptionButton
                key={font.id}
                active={selectionPrefs.fontFamily === font.id}
                onClick={() => applyFont(font.id)}
              >
                <span
                  className="text-sm leading-snug"
                  style={{ fontFamily: font.id }}
                >
                  {font.label}
                </span>
              </StyleOptionButton>
            ))}
          </div>
        </section>
      </div>
    </SideDrawer>
  );
}

export default LetterFormattingDrawer;
